/* eslint-disable no-unused-vars */
import { useEffect, useState } from 'react';
import {
    Box, VStack, HStack, Flex, Text, Heading, Button, Divider, Badge,
    Radio, RadioGroup, Spinner, Center, useToast,
    Modal, ModalOverlay, ModalContent, ModalBody, ModalCloseButton,
} from '@chakra-ui/react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import countries from 'i18n-iso-countries';
import en from 'i18n-iso-countries/langs/en.json';

import { useGetQoutesAndCarriers } from 'hooks/useGetLocations';
import { formatPhoneNumberInternational } from 'utils/phoneformatter';

countries.registerLocale(en);

/* ─────────────────────────────────────────────────────────────
   BRAND TOKENS
   ───────────────────────────────────────────────────────────── */
const NAVY        = '#1B3B5F';
const NAVY_DEEP   = '#0F2940';
const CYAN        = '#DCF8F9';
const INK         = '#1A2740';
const MUTED       = '#5A6B7E';
const FAINT       = '#8896AE';
const SURFACE     = '#FFFFFF';
const SURFACE_ALT = '#F7F9FB';
const BORDER      = '#E8EDF5';
const DANGER      = '#E53E3E';
const SUCCESS     = '#10B981';

const FONT      = "'Bricolage Grotesque', sans-serif";
const SHADOW_SM = '0 1px 3px rgba(27,59,95,0.06)';

/* ─────────────────────────────────────────────────────────────
   ICONS
   ───────────────────────────────────────────────────────────── */
const StoreIcon = (props) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M3 9l1.5-5h15L21 9 M3 9v11h18V9 M3 9h18 M8 14h8"
            stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
);
const TruckIcon = (props) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M1 3h15v13H1z M16 8h4l3 3v5h-7z M5.5 21a2.5 2.5 0 100-5 2.5 2.5 0 000 5z M18.5 21a2.5 2.5 0 100-5 2.5 2.5 0 000 5z"
            stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
);
const CheckIcon = (props) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M20 6L9 17l-5-5"
            stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

/* ─────────────────────────────────────────────────────────────
   STORES — preserved exactly
   ───────────────────────────────────────────────────────────── */
const STORES = [
    {
        id: 'opebi',
        carrier_id: 'opebi',
        name: 'Warehouse Pickup Opebi obana',
        address: {
            line1: '77 Opebi Road',
            city: 'Ikeja',
            state: { label: 'Lagos', value: 'Lagos' },
            country: { label: 'NG', value: 'Nigeria' },
            phone: '+234800335245',
        },
        currency: 'NGN',
        amount: 0,
        delivery_time: 'Same-day Pickup',
        carrier_rate_description: 'Fulfilment Centre Pickup',
    },
];

/* ─────────────────────────────────────────────────────────────
   HELPERS — preserved exactly from original
   ───────────────────────────────────────────────────────────── */
const normalizeStr = (val) => (typeof val === 'string' ? val.trim().toLowerCase() : '');

const getCountryCode = (country) => {
    if (!country) return '';
    if (typeof country === 'string') return country.toUpperCase();
    if (typeof country === 'object' && country.label) return country.label.toUpperCase();
    return '';
};

const normalizePickupAddress = (pickupAddress = {}) => ({
    line1: (pickupAddress.line1 || '').trim().toLowerCase(),
    line2: (pickupAddress.line2 || '').trim().toLowerCase(),
    city: (pickupAddress.city || '').trim().toLowerCase(),
    state: (pickupAddress.state || '').trim().toLowerCase(),
    country: (pickupAddress.country || '').trim().toUpperCase(),
    zip: (pickupAddress.zip_code || pickupAddress.zip || '' || '').trim(),
});

const DispatchOptionsSchema = Yup.object().shape({
    dispatchType: Yup.string().required('Please select a dispatch option'),
    selectedOption: Yup.string().required('Please choose a specific option'),
});

/* ─────────────────────────────────────────────────────────────
   DISPATCH OPTION CARD
   ───────────────────────────────────────────────────────────── */
const DispatchOptionCard = ({
    value, isSelected, onSelect, icon: Icon, title, addressLabel, addressLine, onChangeAddress,
}) => (
    <Box
        role="button"
        tabIndex={0}
        onClick={onSelect}
        onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect?.();
            }
        }}
        bg={SURFACE}
        border="2px solid"
        borderColor={isSelected ? NAVY : BORDER}
        borderRadius="12px"
        p="16px"
        cursor="pointer"
        textAlign="left"
        w="full"
        transition="all 0.15s ease"
        boxShadow={isSelected ? `0 0 0 3px ${CYAN}` : 'none'}
        _hover={{
            borderColor: isSelected ? NAVY : '#CBD5E0',
            transform: 'translateY(-1px)',
        }}
        _focusVisible={{
            outline: `2px solid ${NAVY}`,
            outlineOffset: '2px',
        }}
        aria-pressed={isSelected}
    >
        <Flex gap="14px" align="flex-start">
            {/* Selection indicator */}
            <Flex
                w="22px"
                h="22px"
                borderRadius="full"
                border="2px solid"
                borderColor={isSelected ? NAVY : BORDER}
                bg={isSelected ? NAVY : 'transparent'}
                color="white"
                align="center"
                justify="center"
                flexShrink={0}
                mt="2px"
                transition="all 0.15s ease"
            >
                {isSelected && <CheckIcon />}
            </Flex>

            {/* Body */}
            <Box flex="1" minW="0">
                <Flex align="center" gap="8px" mb="6px">
                    <Box color={NAVY}>
                        <Icon />
                    </Box>
                    <Heading
                        as="h3"
                        fontSize="14px"
                        fontWeight="800"
                        color={INK}
                        fontFamily={FONT}
                        letterSpacing="-0.01em"
                    >
                        {title}
                    </Heading>
                </Flex>

                <Text fontSize="10.5px" color={FAINT} fontWeight="700" letterSpacing="0.06em" mb="4px">
                    {addressLabel}
                </Text>
                <Text fontSize="12px" color={MUTED} lineHeight="1.5" noOfLines={2}>
                    {addressLine}
                </Text>

                {onChangeAddress && (
                    <Button
                        mt="10px"
                        size="xs"
                        variant="outline"
                        borderColor={BORDER}
                        color={NAVY}
                        fontWeight="700"
                        fontSize="11px"
                        h="28px"
                        borderRadius="7px"
                        _hover={{ borderColor: NAVY, bg: SURFACE_ALT }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onChangeAddress();
                        }}
                    >
                        Change address
                    </Button>
                )}
            </Box>
        </Flex>
    </Box>
);

/* ─────────────────────────────────────────────────────────────
   SHIPMENT SUMMARY CARD — inside modal
   ───────────────────────────────────────────────────────────── */
const ShipmentSummaryCard = ({ shipment, index }) => (
    <Box
        bg={SURFACE}
        border="1px solid"
        borderColor={BORDER}
        borderRadius="10px"
        p="14px"
    >
        <Flex justify="space-between" align="flex-start" mb="8px">
            <Text fontWeight="700" fontSize="13px" color={INK} fontFamily={FONT}>
                Shipment {index + 1}
            </Text>
            <Text fontSize="13px" color={NAVY} fontWeight="800" fontFamily={FONT}>
                ₦{(shipment.amount || 0).toLocaleString()}
            </Text>
        </Flex>
        <VStack align="stretch" spacing="4px">
            {(shipment.pickup_address?.line1 || shipment.pickup_address?.city) && (
                <Text fontSize="11px" color={MUTED}>
                    <Text as="span" fontWeight="700" color={FAINT}>Pickup: </Text>
                    {shipment.pickup_address?.line1
                        ? `${shipment.pickup_address.line1}, ${shipment.pickup_address.city}`
                        : shipment.pickup_address?.city}
                </Text>
            )}
            <Text fontSize="11px" color={MUTED}>
                <Text as="span" fontWeight="700" color={FAINT}>Items: </Text>
                {shipment.items?.length || 0}
            </Text>
            {shipment.delivery_time && (
                <Text fontSize="11px" color={MUTED}>
                    <Text as="span" fontWeight="700" color={FAINT}>Delivery estimate: </Text>
                    {shipment.delivery_time}
                </Text>
            )}
            {shipment.carrier_name && (
                <Text fontSize="11px" color={MUTED}>
                    <Text as="span" fontWeight="700" color={FAINT}>Carrier: </Text>
                    {shipment.carrier_name}
                </Text>
            )}
        </VStack>
    </Box>
);

/* ─────────────────────────────────────────────────────────────
   MAIN
   ───────────────────────────────────────────────────────────── */
const DispatchOptions = ({ setTabIndex, setCheckoutPayload, checkoutPayload, fetchQuote }) => {
    const {
        routeMatch,
        bestCarrier,
        setBestCarrier,
        isLoading: carriersLoading,
    } = useGetQoutesAndCarriers();

    const toast = useToast();

    /* State — preserved exactly */
    const [dispatchType, setDispatchType] = useState('');
    const [isDeliveryModalOpen, setIsDeliveryModalOpen] = useState(false);
    const [selectedDeliveryService, setSelectedDeliveryService] = useState('');
    const [carriersFetched, setCarriersFetched] = useState(false);
    const [showCarrierSelection, setShowCarrierSelection] = useState(false);
    const [selectedCarrier, setSelectedCarrier] = useState(null);
    const [mode, setMode] = useState('lagos');
    const [noCarriersAvailable, setNoCarriersAvailable] = useState(false);
    const [deliveryType, setDeliveryType] = useState(null);
    const [vendorGroups, setVendorGroups] = useState(null);
    const [viewMode, setViewMode] = useState('aggregated');
    const [perVendorSelections, setPerVendorSelections] = useState({});
    const [aggregatedRates, setAggregatedRates] = useState([]);
    const [selectedAggregatedRate, setSelectedAggregatedRate] = useState(null);

    /* ─────────────────────────────────────────────────────────
       HELPERS — preserved exactly
       ───────────────────────────────────────────────────────── */
    const formatStoreAddressForPayload = (store) => ({
        first_name: store.contact_first_name || 'Obana',
        last_name: store.contact_last_name || 'Africa',
        email: checkoutPayload?.address?.email || 'obana.africa@gmail.com',
        phone: store.address.phone || '',
        is_residential: false,
        line1: store.address.line1,
        line2: store.address.line2 || '77, opebi street, ikeja, lagos',
        city: store.address.city,
        state: store.address.state?.value || store.address.state,
        country: store.address.country.label,
        zip: store.address.zip || '100001',
    });

    const groupItemsByPickupAddress = (items = []) => {
        const groups = {};
        items.forEach((item) => {
            let country = countries.getAlpha2Code(item.cf_country, 'en');
            const itemPickup = {
                ...{ line1: item.line_address, city: item.cf_city, state: item.cf_state },
                line2: item?.pickup_address?.line2 || item?.line2 || '',
                country,
                contact_name: item?.vendor || 'obana',
            };

            const key = JSON.stringify(normalizePickupAddress(itemPickup));
            const groupPhone =
                formatPhoneNumberInternational(item?.phone, itemPickup.country)
                    ?.split(' ')
                    .join('') ||
                item?.pickup_address?.phone ||
                '';

            if (!groups[key]) {
                groups[key] = {
                    pickup_address: {
                        ...itemPickup,
                        phone: groupPhone,
                    },
                    items: [],
                };
            }

            groups[key].items.push({
                name: item?.name,
                description: item?.description ?? item?.name,
                currency: 'NGN',
                value: item?.total_price ?? 0,
                weight: item?.cf_weight ? item?.cf_weight : 1,
                item_id: item?.item_id,
                quantity: item?.quantity ?? 1,
                pickup_address: {
                    ...groups[key].pickup_address,
                },
            });
        });

        return Object.values(groups).flatMap((group) => group.items);
    };

    const resolveMode = (checkoutPayload, dispatchType) => {
        const items = checkoutPayload?.cartItems?.items || [];
        const deliveryAddress =
            dispatchType === 'pickup' ? STORES[0].address : checkoutPayload?.address;

        const deliveryState =
            dispatchType === 'pickup'
                ? normalizeStr(deliveryAddress?.state?.label || deliveryAddress?.state)
                : normalizeStr(deliveryAddress?.state);

        const deliveryCountry = getCountryCode(deliveryAddress?.country);

        let hasInternational = false;
        let hasNationwide = false;
        let hasLagos = false;

        for (const item of items) {
            const vendorAddr = {
                line1: item.line_address,
                city: item.cf_city,
                state: item.cf_state,
                country: item.cf_country,
            };
            const vendorState = normalizeStr(vendorAddr.state);
            const vendorCountry = getCountryCode(vendorAddr.country);

            if (
                (vendorCountry && deliveryCountry && vendorCountry !== deliveryCountry) ||
                vendorCountry !== 'NG' ||
                deliveryCountry !== 'NG'
            ) {
                hasInternational = true;
            } else if (
                deliveryCountry === 'NG' &&
                vendorCountry === 'NG' &&
                vendorState &&
                deliveryState &&
                (vendorState !== deliveryState ||
                    vendorState !== 'lagos' ||
                    deliveryState !== 'lagos')
            ) {
                hasNationwide = true;
            } else if (
                deliveryCountry === 'NG' &&
                vendorCountry === 'NG' &&
                normalizeStr(vendorState) === 'lagos' &&
                normalizeStr(deliveryState) === 'lagos'
            ) {
                hasLagos = true;
            }
        }

        if (hasInternational) return 'international';
        if (hasNationwide) return 'nationwide';
        if (hasLagos) return 'lagos';
        return 'lagos';
    };

    const checkIfSameAddress = async (items, deliveryAddress) => {
        if (!Array.isArray(items) || items.length === 0 || !deliveryAddress) {
            return false;
        }

        try {
            const deliveryAddr = {
                line1: deliveryAddress?.line1 || '',
                city: deliveryAddress?.city || '',
                state: deliveryAddress?.state?.label || deliveryAddress?.state || '',
                country:
                    typeof deliveryAddress?.country === 'object'
                        ? deliveryAddress?.country?.label || deliveryAddress?.country?.value
                        : deliveryAddress?.country,
            };

            for (let item of items) {
                const vendorAddr = {
                    line1: item.line_address,
                    city: item.cf_city,
                    state: item.cf_state,
                    country: item.cf_country,
                };
                const sameLine1 = normalizeStr(vendorAddr.line1) === normalizeStr(deliveryAddr.line1);
                const sameCity = normalizeStr(vendorAddr.city) === normalizeStr(deliveryAddr.city);
                const sameState = normalizeStr(vendorAddr.state) === normalizeStr(deliveryAddr.state);
                const sameCountry = normalizeStr(vendorAddr.country) === normalizeStr(deliveryAddr.country);

                if (!(sameLine1 && sameCity && sameState && sameCountry)) {
                    return false;
                }
            }
            return true;
        } catch (error) {
            console.error('Error comparing addresses:', error);
            return false;
        }
    };

    const handleAutoDispatch = async (type, setFieldValue) => {
        const items = checkoutPayload?.cartItems?.items || [];
        const deliveryAddress = type === 'pickup' ? STORES[0].address : checkoutPayload?.address;

        const isSameAddress = await checkIfSameAddress(items, deliveryAddress);
        const pickUpMethod = isSameAddress ? 'fulfilment_centre' : 'delivery';

        if (isSameAddress) {
            const dispatcher =
                type === 'pickup'
                    ? STORES[0]
                    : {
                          carrier_id: 'self-delivery',
                          carrier_name: 'Self Delivery',
                          amount: 0,
                          delivery_time: 'N/A',
                          carrier_rate_description: 'No delivery needed',
                      };

            await setCheckoutPayload((prev) => ({
                ...prev,
                dispatcher,
                pickUpMethod,
                delivery_type: type === 'pickup' ? 'pickup' : 'self-delivery',
            }));

            setFieldValue('dispatchType', type);
            setFieldValue('selectedOption', dispatcher.carrier_id || dispatcher.id);

            setTimeout(() => setTabIndex(2), 100);
            return true;
        }

        await setCheckoutPayload((prev) => ({ ...prev, pickUpMethod }));
        return false;
    };

    const closeDeliveryModal = () => {
        setIsDeliveryModalOpen(false);
        setSelectedDeliveryService('');
        setShowCarrierSelection(false);
        setCarriersFetched(false);
        setSelectedCarrier(null);
        setDeliveryType(null);
        setVendorGroups(null);
        setPerVendorSelections({});
        setAggregatedRates([]);
        setSelectedAggregatedRate(null);
        setNoCarriersAvailable(false);
        setBestCarrier(null);
    };

    /* ─────────────────────────────────────────────────────────
       handleDeliveryServiceSelect — NULL GUARD ADDED
       ───────────────────────────────────────────────────────── */
    const handleDeliveryServiceSelect = async (serviceId, typeOverride) => {
        const currentType = typeOverride || dispatchType;
        const isPickup = currentType === 'pickup';

        /* CRITICAL FIX — guard against missing address on delivery */
        if (!isPickup && !checkoutPayload?.address) {
            toast({
                title: 'Address required',
                description: 'Please complete the delivery address first.',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
            closeDeliveryModal();
            setTabIndex(0);
            return;
        }

        setSelectedDeliveryService(serviceId);
        setCarriersFetched(false);
        setShowCarrierSelection(false);
        setNoCarriersAvailable(false);
        setBestCarrier(null);
        setAggregatedRates([]);
        setSelectedAggregatedRate(null);
        setDeliveryType(null);
        setVendorGroups(null);
        setPerVendorSelections({});
        setSelectedCarrier(null);
        setViewMode('aggregated');

        const detectedMode = resolveMode(checkoutPayload, currentType);
        setMode(detectedMode);

        /* Safely extract country with null guards */
        const addressCountry = checkoutPayload?.address?.country;
        const useExisting = checkoutPayload?.address?.useExistingAddress;
        let delCountry = useExisting
            ? countries.getAlpha2Code(addressCountry, 'en')
            : addressCountry;

        const deliveryAddress = isPickup
            ? formatStoreAddressForPayload(STORES[0])
            : {
                  ...checkoutPayload?.address,
                  country: delCountry,
                  phone: formatPhoneNumberInternational(
                      checkoutPayload?.address?.phone,
                      checkoutPayload?.address?.country
                  )?.split(' ').join(''),
              };

        /* Items validation + fallback — preserved */
        const FALLBACK_ADDRESS = {
            line1: '77 opebi road',
            city: 'ikeja',
            state: 'lagos',
            country: 'NG',
        };

        const itemsWithIssues = [];
        const validatedItems = checkoutPayload?.cartItems?.items?.map((item) => {
            const missingFields = [];
            if (!item.line_address || item.line_address.trim() === '') missingFields.push('line1');
            if (!item.cf_city || item.cf_city.trim() === '') missingFields.push('city');
            if (!item.cf_state || item.cf_state.trim() === '') missingFields.push('state');
            if (!item.cf_country || item.cf_country.trim() === '') missingFields.push('country');

            if (missingFields.length > 0) {
                itemsWithIssues.push({ name: item.name, missingFields });
                return {
                    ...item,
                    line_address: item.line_address || FALLBACK_ADDRESS.line1,
                    cf_city: item.cf_city || FALLBACK_ADDRESS.city,
                    cf_state: item.cf_state || FALLBACK_ADDRESS.state,
                    cf_country: item.cf_country || FALLBACK_ADDRESS.country,
                };
            }
            return item;
        }) || [];

        const payload = {
            delivery_address: deliveryAddress,
            currency: 'NGN',
            parcel: {
                description: 'obana logistics shipment',
                packaging: '',
                weight_unit: 'kg',
                items: groupItemsByPickupAddress(validatedItems),
            },
        };

        try {
            const result = await routeMatch(payload);
            const shipments = Array.isArray(result) ? result : result ? [result] : [];

            if (!shipments.length) {
                setNoCarriersAvailable(true);
                setCarriersFetched(true);
                return;
            }

            setAggregatedRates(shipments);
            setSelectedAggregatedRate(
                shipments[0]?.rate_id || shipments[0]?.id || 'obana_logistics'
            );

            const totalDeliveryCost = shipments.reduce(
                (sum, shipment) => sum + (shipment.amount || 0),
                0
            );
            if (fetchQuote) fetchQuote(totalDeliveryCost);

            setDeliveryType(shipments.length > 1 ? 'multi-shipment' : 'single-shipment');
            setCarriersFetched(true);
            setShowCarrierSelection(true);
        } catch (err) {
            console.error('Error in handleDeliveryServiceSelect:', err);
            setNoCarriersAvailable(true);
            setCarriersFetched(true);
        }
    };

    const confirmCarrierSelection = (setFieldValue) => {
        if (!aggregatedRates || aggregatedRates.length === 0) {
            toast({
                title: 'Selection error',
                description: 'No shipment details available to confirm.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        const totalAmount = aggregatedRates.reduce(
            (sum, shipment) => sum + (shipment.amount || 0),
            0
        );
        const dispatcher = {
            carrier_id: 'obana_logistics',
            carrier_name: 'Obana Logistics',
            amount: totalAmount,
            delivery_time:
                aggregatedRates.length > 1
                    ? 'Multiple shipments'
                    : aggregatedRates[0]?.delivery_time || 'Obana Logistics delivery',
            carrier_rate_description:
                aggregatedRates.length > 1
                    ? `${aggregatedRates.length} grouped shipments`
                    : aggregatedRates[0]?.carrier_rate_description || 'Obana Logistics delivery',
            shipments: aggregatedRates,
        };

        setCheckoutPayload((prev) => ({
            ...prev,
            dispatcher,
            rate_id: selectedAggregatedRate || 'obana_logistics',
            selectedCarrierReference: aggregatedRates[0]?.carrier_reference || 'obana_logistics',
            vendorGroups: aggregatedRates,
            delivery_type: deliveryType || (aggregatedRates.length > 1 ? 'aggregated' : 'single'),
            isMultiVendor: aggregatedRates.length > 1,
        }));

        if (setFieldValue) {
            setFieldValue('selectedOption', 'obana_logistics');
        }

        setTimeout(() => {
            closeDeliveryModal();
            setTabIndex(2);
        }, 100);
    };

    const handleSubmit = (values, { setSubmitting }) => {
        try {
            let dispatcher = null;
            let rateId = null;
            let finalDeliveryType = null;
            let vendorGroups = null;
            let selectedCarrierReference = null;
            let isMultiVendor = false;

            if (!checkoutPayload?.pickUpMethod) {
                const items = checkoutPayload?.cartItems?.items || [];
                const deliveryAddress =
                    values?.dispatchType === 'pickup'
                        ? STORES[0].address
                        : checkoutPayload?.address;

                checkIfSameAddress(items, deliveryAddress).then((isSameAddress) => {
                    const pickUpMethod = isSameAddress ? 'fulfilment_centre' : 'delivery';
                    setCheckoutPayload((prev) => ({ ...prev, pickUpMethod }));
                });
            }

            if (values?.dispatchType === 'pickup') {
                dispatcher = checkoutPayload.dispatcher || STORES[0];
                finalDeliveryType = checkoutPayload.delivery_type || 'pickup';
            } else if (values?.dispatchType === 'delivery') {
                dispatcher = checkoutPayload.dispatcher;
                finalDeliveryType = checkoutPayload.delivery_type;
                rateId = checkoutPayload.rate_id;
                vendorGroups = checkoutPayload.vendorGroups;
                selectedCarrierReference = checkoutPayload.selectedCarrierReference;
                isMultiVendor = checkoutPayload.isMultiVendor;

                if (!dispatcher) {
                    toast({
                        title: 'Selection error',
                        description: 'Please select a valid dispatch option',
                        status: 'error',
                        duration: 3000,
                        isClosable: true,
                    });
                    setSubmitting(false);
                    return;
                }

                if (!finalDeliveryType) {
                    finalDeliveryType = 'single';
                }
            }

            if (!dispatcher) {
                toast({
                    title: 'Selection error',
                    description: 'Please select a valid dispatch option',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
                setSubmitting(false);
                return;
            }

            const payload = {
                dispatcher,
                rate_id: rateId,
                dispatchType: values.dispatchType,
                delivery_type: finalDeliveryType,
            };

            if (finalDeliveryType === 'per-vendor' && vendorGroups) {
                payload.vendorGroups = vendorGroups;
                payload.isMultiVendor = true;
            }

            if (finalDeliveryType === 'aggregated' && vendorGroups) {
                payload.vendorGroups = vendorGroups;
                payload.isMultiVendor = true;
                payload.selectedCarrierReference = selectedCarrierReference;
            }

            if (isMultiVendor) {
                payload.isMultiVendor = isMultiVendor;
            }

            setCheckoutPayload((prev) => ({ ...prev, ...payload }));
            setTabIndex(2);
            setSubmitting(false);
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Something went wrong while processing your selection',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            setSubmitting(false);
        }
    };

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    /* ─────────────────────────────────────────────────────────
       DELIVERY SUMMARY (shown under delivery card when selected)
       ───────────────────────────────────────────────────────── */
    const renderDeliverySummary = () => {
        const { delivery_type, dispatcher, vendor_selections, vendorGroups } = checkoutPayload;

        if (delivery_type === 'per-vendor' && vendor_selections && vendor_selections.length > 0) {
            const totalCost = vendor_selections.reduce((sum, sel) => sum + sel.cost, 0);
            return (
                <VStack align="start" spacing="2px">
                    <Text fontSize="12px" color={INK} fontWeight="700">
                        Multiple carriers · ₦{totalCost.toLocaleString()}
                    </Text>
                    <Text fontSize="10.5px" color={MUTED}>
                        {vendor_selections.length} vendors configured
                    </Text>
                </VStack>
            );
        }

        if (delivery_type === 'aggregated' && dispatcher) {
            return (
                <VStack align="start" spacing="2px">
                    <Text fontSize="12px" color={INK} fontWeight="700">
                        {dispatcher.carrier_name || 'Delivery service'}
                    </Text>
                    <Text fontSize="10.5px" color={MUTED}>
                        Unified delivery for {vendorGroups?.length || 1} vendors
                    </Text>
                </VStack>
            );
        }

        if (delivery_type === 'single' && dispatcher) {
            return (
                <VStack align="start" spacing="2px">
                    <Text fontSize="12px" color={INK} fontWeight="700">
                        {dispatcher.carrier_name || 'Delivery service'}
                    </Text>
                    <Text fontSize="10.5px" color={MUTED}>
                        Single vendor delivery
                    </Text>
                </VStack>
            );
        }

        if (dispatcher?.carrier_id === 'self-delivery') {
            return (
                <VStack align="start" spacing="2px">
                    <Text fontSize="12px" color={INK} fontWeight="700">
                        Self delivery · ₦0
                    </Text>
                    <Text fontSize="10.5px" color={MUTED}>
                        No delivery needed
                    </Text>
                </VStack>
            );
        }

        return null;
    };

    /* ─────────────────────────────────────────────────────────
       Total aggregated cost for modal header
       ───────────────────────────────────────────────────────── */
    const totalAggregatedAmount = aggregatedRates?.reduce(
        (sum, shipment) => sum + (shipment.amount || 0),
        0
    ) ?? 0;

    /* Address summary lines for delivery card */
    const deliveryAddressLine =
        checkoutPayload?.address
            ? `${checkoutPayload.address.line1 || ''}${
                  checkoutPayload.address.city ? `, ${checkoutPayload.address.city}` : ''
              }${
                  checkoutPayload.address.state ? `, ${checkoutPayload.address.state}` : ''
              }${
                  checkoutPayload.address.country ? `, ${checkoutPayload.address.country}` : ''
              }`.trim()
            : 'No address provided yet';

    const pickupAddressLine = [
        STORES[0].address.line1,
        STORES[0].address.city,
        STORES[0].address.state?.label,
        STORES[0].address.country?.value,
    ].filter(Boolean).join(', ');

    /* ─────────────────────────────────────────────────────────
       RENDER
       ───────────────────────────────────────────────────────── */
    return (
        <Box py="20px" fontFamily={FONT}>
            <Formik
                initialValues={{ dispatchType: '', selectedOption: '' }}
                validationSchema={DispatchOptionsSchema}
                onSubmit={handleSubmit}
            >
                {({ values, errors, touched, isSubmitting, setFieldValue }) => (
                    <Form>
                        <VStack spacing="20px" align="stretch">
                            {/* Heading */}
                            <Box>
                                <Heading
                                    as="h2"
                                    fontSize={{ base: '18px', md: '20px' }}
                                    fontWeight="800"
                                    color={INK}
                                    fontFamily={FONT}
                                    letterSpacing="-0.02em"
                                    mb="4px"
                                >
                                    Choose dispatch option
                                </Heading>
                                <Text fontSize="12px" color={MUTED}>
                                    Pickup from our warehouse or have it delivered
                                </Text>
                            </Box>

                            {/* Dispatch option cards */}
                            <Field name="dispatchType">
                                {({ field }) => (
                                    <RadioGroup
                                        {...field}
                                        onChange={(val) => {
                                            setDispatchType(val);
                                            field.onChange('dispatchType')(val);
                                            setFieldValue('selectedOption', '');
                                        }}
                                    >
                                        <VStack align="stretch" spacing="12px">
                                            {/* Fulfilment Centre Pickup */}
                                            <DispatchOptionCard
                                                value="pickup"
                                                isSelected={values.dispatchType === 'pickup'}
                                                icon={StoreIcon}
                                                title="Fulfilment centre pickup"
                                                addressLabel={STORES[0].name.toUpperCase()}
                                                addressLine={pickupAddressLine}
                                                onSelect={async () => {
                                                    setDispatchType('pickup');
                                                    field.onChange('dispatchType')('pickup');
                                                    setFieldValue('selectedOption', '');
                                                    const wasAutoDispatched = await handleAutoDispatch(
                                                        'pickup',
                                                        setFieldValue
                                                    );
                                                    if (!wasAutoDispatched) {
                                                        setIsDeliveryModalOpen(true);
                                                        await handleDeliveryServiceSelect(
                                                            'obana_logistics',
                                                            'pickup'
                                                        );
                                                    }
                                                }}
                                            />

                                            {/* Door Delivery */}
                                            <DispatchOptionCard
                                                value="delivery"
                                                isSelected={values.dispatchType === 'delivery'}
                                                icon={TruckIcon}
                                                title="Door delivery"
                                                addressLabel="DELIVERY ADDRESS"
                                                addressLine={deliveryAddressLine}
                                                onChangeAddress={() => setTabIndex(0)}
                                                onSelect={async () => {
                                                    /* Guard at click site too */
                                                    if (!checkoutPayload?.address) {
                                                        toast({
                                                            title: 'Address required',
                                                            description: 'Please complete the delivery address first.',
                                                            status: 'warning',
                                                            duration: 3000,
                                                            isClosable: true,
                                                        });
                                                        setTabIndex(0);
                                                        return;
                                                    }
                                                    setDispatchType('delivery');
                                                    field.onChange('dispatchType')('delivery');
                                                    setFieldValue('selectedOption', '');
                                                    setIsDeliveryModalOpen(true);
                                                    await handleDeliveryServiceSelect(
                                                        'obana_logistics',
                                                        'delivery'
                                                    );
                                                }}
                                            />
                                        </VStack>
                                    </RadioGroup>
                                )}
                            </Field>

                            {/* Selected delivery summary */}
                            {values.dispatchType === 'delivery' && values.selectedOption && (
                                <Box
                                    bg={SURFACE_ALT}
                                    border="1px solid"
                                    borderColor={BORDER}
                                    borderRadius="10px"
                                    p="12px 14px"
                                >
                                    <Text
                                        fontSize="10px"
                                        color={FAINT}
                                        fontWeight="700"
                                        letterSpacing="0.08em"
                                        mb="6px"
                                    >
                                        SELECTED DELIVERY SERVICE
                                    </Text>
                                    {renderDeliverySummary()}
                                </Box>
                            )}

                            {/* Error messages */}
                            {errors.dispatchType && touched.dispatchType && (
                                <Text fontSize="11px" color={DANGER} fontWeight="500">
                                    {errors.dispatchType}
                                </Text>
                            )}
                            {errors.selectedOption && touched.selectedOption && (
                                <Text fontSize="11px" color={DANGER} fontWeight="500">
                                    {errors.selectedOption}
                                </Text>
                            )}

                            {/* Submit */}
                            <Button
                                type="submit"
                                w="full"
                                h="46px"
                                bg={NAVY}
                                color="white"
                                _hover={{ bg: NAVY_DEEP }}
                                _active={{ bg: NAVY_DEEP }}
                                _disabled={{
                                    bg: BORDER,
                                    color: FAINT,
                                    cursor: 'not-allowed',
                                    _hover: { bg: BORDER },
                                }}
                                borderRadius="10px"
                                fontWeight="700"
                                fontSize="13px"
                                fontFamily={FONT}
                                isLoading={isSubmitting}
                                loadingText="Processing"
                                isDisabled={
                                    !values.dispatchType ||
                                    (values.dispatchType === 'delivery' &&
                                        !values.selectedOption &&
                                        !selectedCarrier &&
                                        !selectedAggregatedRate)
                                }
                            >
                                Proceed
                            </Button>
                        </VStack>

                        {/* ─── DELIVERY MODAL (Chakra) ─── */}
                        <Modal
                            isOpen={isDeliveryModalOpen}
                            onClose={closeDeliveryModal}
                            isCentered
                            motionPreset="slideInBottom"
                            scrollBehavior="inside"
                            size={{ base: 'full', sm: 'lg' }}
                        >
                            <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(2px)" />
                            <ModalContent
                                bg={SURFACE}
                                borderRadius={{ base: '16px 16px 0 0', sm: '16px' }}
                                fontFamily={FONT}
                                mx={{ base: '0', sm: '14px' }}
                                mb={{ base: '0', sm: 'auto' }}
                                mt={{ base: 'auto', sm: '60px' }}
                                maxH={{ base: '92vh', sm: '85vh' }}
                                boxShadow="0 24px 60px rgba(27,59,95,0.18)"
                                overflow="hidden"
                            >
                                {/* Header */}
                                <Box p="20px 24px 16px" borderBottom="1px solid" borderColor={BORDER}>
                                    <Flex justify="space-between" align="flex-start" gap="14px">
                                        <Box minW="0" flex="1">
                                            <Heading
                                                as="h2"
                                                fontSize={{ base: '17px', md: '19px' }}
                                                fontWeight="800"
                                                color={INK}
                                                fontFamily={FONT}
                                                letterSpacing="-0.02em"
                                                lineHeight="1.2"
                                                mb="4px"
                                            >
                                                {carriersFetched && !noCarriersAvailable
                                                    ? 'Shipment summary'
                                                    : 'Choose delivery service'}
                                            </Heading>
                                            <Text fontSize="12px" color={MUTED}>
                                                {carriersFetched && !noCarriersAvailable
                                                    ? 'Review your shipment details before proceeding'
                                                    : 'Select your preferred delivery option'}
                                            </Text>
                                        </Box>
                                        <ModalCloseButton
                                            position="static"
                                            color={MUTED}
                                            borderRadius="8px"
                                            _hover={{ bg: SURFACE_ALT, color: INK }}
                                        />
                                    </Flex>
                                </Box>

                                {/* Body */}
                                <ModalBody p="20px 24px">
                                    {(!carriersFetched && !noCarriersAvailable) || carriersLoading ? (
                                        <Center py="40px" flexDirection="column" gap="14px">
                                            <Spinner size="lg" color={NAVY} thickness="3px" />
                                            <Text fontSize="13px" color={MUTED}>
                                                Fetching Obana Logistics shipment details…
                                            </Text>
                                        </Center>
                                    ) : noCarriersAvailable ? (
                                        <Center py="40px">
                                            <VStack spacing="10px" maxW="380px" textAlign="center">
                                                <Text fontSize="14px" fontWeight="700" color={INK}>
                                                    No shipment details available
                                                </Text>
                                                <Text fontSize="12px" color={MUTED}>
                                                    Please verify your delivery address or try again.
                                                </Text>
                                                <Button
                                                    mt="8px"
                                                    size="sm"
                                                    bg={NAVY}
                                                    color="white"
                                                    _hover={{ bg: NAVY_DEEP }}
                                                    borderRadius="8px"
                                                    fontSize="11px"
                                                    fontFamily={FONT}
                                                    onClick={() => {
                                                        closeDeliveryModal();
                                                        setTabIndex(0);
                                                    }}
                                                >
                                                    Change address
                                                </Button>
                                            </VStack>
                                        </Center>
                                    ) : (
                                        <VStack align="stretch" spacing="14px">
                                            {/* Summary bar */}
                                            <Box bg={SURFACE_ALT} p="14px" borderRadius="10px"
                                                border="1px solid" borderColor={BORDER}>
                                                <Flex justify="space-between" align="flex-start" gap="14px">
                                                    <Box flex="1" minW="0">
                                                        <Text fontSize="10px" color={FAINT}
                                                            fontWeight="700" letterSpacing="0.08em" mb="4px">
                                                            OBANA LOGISTICS
                                                        </Text>
                                                        <Text fontSize="12px" color={MUTED} lineHeight="1.4">
                                                            {aggregatedRates?.length > 1
                                                                ? `${aggregatedRates.length} grouped shipments`
                                                                : 'Single shipment'}
                                                        </Text>
                                                    </Box>
                                                    <Box textAlign="right" flexShrink={0}>
                                                        <Text fontSize="10px" color={FAINT}
                                                            fontWeight="700" letterSpacing="0.06em" mb="2px">
                                                            TOTAL
                                                        </Text>
                                                        <Text fontSize="18px" color={NAVY}
                                                            fontWeight="800" fontFamily={FONT} lineHeight="1.1">
                                                            ₦{totalAggregatedAmount.toLocaleString()}
                                                        </Text>
                                                    </Box>
                                                </Flex>
                                            </Box>

                                            {/* Per-shipment cards */}
                                            {aggregatedRates?.map((shipment, index) => (
                                                <ShipmentSummaryCard
                                                    key={shipment.id || shipment.rate_id || index}
                                                    shipment={shipment}
                                                    index={index}
                                                />
                                            ))}

                                            {/* Change address link */}
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                color={NAVY}
                                                fontWeight="700"
                                                fontSize="11.5px"
                                                fontFamily={FONT}
                                                h="32px"
                                                _hover={{ bg: SURFACE_ALT }}
                                                onClick={() => {
                                                    closeDeliveryModal();
                                                    setTabIndex(0);
                                                }}
                                            >
                                                Change address
                                            </Button>
                                        </VStack>
                                    )}
                                </ModalBody>

                                {/* Footer */}
                                <Box
                                    p="14px 24px 20px"
                                    borderTop="1px solid"
                                    borderColor={BORDER}
                                    bg={SURFACE}
                                >
                                    <HStack spacing="10px">
                                        <Button
                                            flex={1}
                                            variant="outline"
                                            borderColor={BORDER}
                                            color={INK}
                                            fontWeight="600"
                                            fontSize="13px"
                                            fontFamily={FONT}
                                            h="44px"
                                            borderRadius="10px"
                                            _hover={{ bg: SURFACE_ALT, borderColor: NAVY }}
                                            onClick={closeDeliveryModal}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            flex={1}
                                            bg={NAVY}
                                            color="white"
                                            fontWeight="700"
                                            fontSize="13px"
                                            fontFamily={FONT}
                                            h="44px"
                                            borderRadius="10px"
                                            _hover={{ bg: NAVY_DEEP }}
                                            _active={{ bg: NAVY_DEEP }}
                                            _disabled={{
                                                bg: BORDER,
                                                color: FAINT,
                                                cursor: 'not-allowed',
                                                _hover: { bg: BORDER },
                                            }}
                                            isDisabled={
                                                !carriersFetched ||
                                                noCarriersAvailable ||
                                                !aggregatedRates?.length
                                            }
                                            onClick={() => {
                                                if (
                                                    carriersFetched &&
                                                    !noCarriersAvailable &&
                                                    aggregatedRates?.length > 0
                                                ) {
                                                    confirmCarrierSelection(setFieldValue);
                                                }
                                            }}
                                        >
                                            Confirm & proceed
                                        </Button>
                                    </HStack>
                                </Box>
                            </ModalContent>
                        </Modal>
                    </Form>
                )}
            </Formik>
        </Box>
    );
};

export default DispatchOptions;